<template>
  <div>
    <card>
      <tab-rail class="mb-3" :tabs="tabs" :value="activeTab" @input="onTab">
        <template #actions>
          <template v-if="activeTab === RATINGS_TAB">
            <base-button class="d-none d-sm-inline-block" type="primary" size="sm" @click="addRatingScale">
              <i class="tim-icons icon-simple-add mr-1"></i> Add rating scale
            </base-button>
            <base-button class="d-sm-none" type="primary" size="sm" icon title="Add rating scale" @click="addRatingScale">
              <i class="tim-icons icon-simple-add"></i>
            </base-button>
          </template>
          <template v-else>
            <base-button class="d-none d-sm-inline-block" type="primary" size="sm" @click="addGame">
              <i class="tim-icons icon-simple-add mr-1"></i> Add new event
            </base-button>
            <base-button class="d-sm-none" type="primary" size="sm" icon title="Add new event" @click="addGame">
              <i class="tim-icons icon-simple-add"></i>
            </base-button>
          </template>
        </template>
      </tab-rail>

      <rating-scales-panel v-if="activeTab === RATINGS_TAB" ref="ratingsPanel" @changed="loadRatingScales" />

      <div v-show="activeTab !== RATINGS_TAB">

      <filter-bar
        :filters="filterSchema"
        :initial-value="filterValues"
        :loading="refreshing"
        :result-count="games.length"
        saved-filters-key="game-settings"
        @change="onFiltersChanged"
      >
        <template #actions>
          <base-button v-if="canDownload" icon class="btn-link text-white" title="Download CSV" @click="downloadEvents">
            <i class="tim-icons icon-cloud-download-93"></i>
          </base-button>
        </template>
      </filter-bar>

      <data-table
        class="mt-3"
        :columns="columns"
        :rows="games"
        row-key="id"
        :loading="refreshing"
        :reorderable="canReorder"
        @reorder="onReorder"
      >
        <template #cell(name)="{ row }">
          <span class="data-table__thumb">
            <img v-if="row.url" v-twin-src="row.url" alt="" />
            <span class="data-table__primary">{{ row.name }}</span>
          </span>
        </template>
        <template #cell(duration_minutes)="{ row }">
          <span v-if="row.duration_minutes">{{ row.duration_minutes }} minutes</span>
          <span v-else class="data-table__muted">-</span>
        </template>
        <template #cell(rating)="{ row }">
          <!-- Every rating, shown to customers or not. Scale name then rating, one pair per line; the grid
               lines up every rating at the same edge. A "Hidden" tag marks ratings customers do not see.
               Two or more ratings drop to the smaller size. -->
          <div v-if="row.ratings && row.ratings.length" class="data-table__label-grid">
            <template v-for="r in row.ratings">
              <span :key="'n' + r.rating_scale_id" class="data-table__muted small">{{ r.name }}</span>
              <span :key="'v' + r.rating_scale_id" class="d-inline-flex align-items-center">
                <rating-display :value="r.value" :scale="r" :size="row.ratings.length > 1 ? 'xs' : 'sm'" />
                <span v-if="!r.show_to_customers" class="tag-chip tag-chip--sm text-white ml-2" title="Not shown to customers">Hidden</span>
              </span>
            </template>
          </div>
          <span v-else class="data-table__muted">-</span>
        </template>
        <template #cell(players)="{ row }">
          <span>{{ row.min_players_count }} - {{ row.max_players_count }}</span>
        </template>
        <template #actions="{ row }">
          <el-dropdown>
            <base-button simple type="primary" link size="sm">
              <i class="tim-icons el-icon-more"></i>
            </base-button>
            <template #dropdown>
              <el-dropdown-menu>
                <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="editGame(row)">
                  <i class="tim-icons text-default icon-pencil mr-2"></i> Edit event
                </a>
                <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="duplicateGame(row)">
                  <i class="tim-icons text-default icon-single-copy-04 mr-2"></i> Duplicate event
                </a>
                <hr>
                <a v-if="row.archived" class="dropdown-item" href="#" @click.prevent="toggleActivationPrompt(row)">
                  <span class="active-dot mr-2" style="padding: 0; margin-left: -3px; margin-bottom: -3px"></span> Activate event
                </a>
                <a v-else class="dropdown-item" href="#" @click.prevent="toggleActivationPrompt(row)">
                  <span class="inactive-dot mr-2" style="padding: 0; margin-left: -3px; margin-bottom: -3px"></span> Deactivate event
                </a>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
        <template #empty>
          <img :src="emptyState.image" alt="" class="empty-state-img empty-state-img--md" />
          <h4 class="mt-3 empty-state-text">{{ emptyState.title }}</h4>
          <p class="data-table__muted">{{ emptyState.text }}</p>
        </template>
      </data-table>
      </div>
    </card>

    <base-alert-dialog
      :show="dialog.show"
      :variant="dialog.variant"
      :title="dialog.title"
      :message="dialog.message"
      :show-cancel="dialog.showCancel"
      :confirm-text="dialog.confirmText"
      :confirm-variant="dialog.confirmVariant"
      @confirm="onDialogConfirm"
      @cancel="dialog.show = false"
    />
  </div>
</template>

<script>
  // Events list: GET /companies/{id}/groups/{grid}/games with status, name,
  // rating_scale_id + rating, duration_from, duration_to and public applied by the lambda.
  // The whole tab loads in one request in stored order, never paged or sorted,
  // because the list is manually ordered and a drag has to span every row.
  // Drag reorder is always available on the Active tab and persists through
  // the order endpoint, which assigns position by array index over the full
  // active list.
  import { Card } from 'src/components'
  import { Auth } from 'aws-amplify'
  import { API_LOCATION } from '../../../api-config'
  import axios from 'axios'
  import { Dropdown, DropdownItem, DropdownMenu } from 'element-ui'
  import TabRail from '@/components/TabRail.vue'
  import RatingScalesPanel from './components/RatingScalesPanel.vue'
  import { downloadCsv } from '@/util/listPage'

  const PLACEHOLDER_IMG = 'https://console.offthecouch.io/img/placeholder.jpg'

  export default {
    name: 'GameSetup',
    components: {
      Card,
      TabRail,
      RatingScalesPanel,
      [Dropdown.name]: Dropdown,
      [DropdownMenu.name]: DropdownMenu,
      [DropdownItem.name]: DropdownItem,
    },
    data() {
      return {
        refreshing: false,
        didInitialFetch: false,
        games: [],
        activeTab: 0,
        tabs: [{ name: 'Events' }, { name: 'Ratings' }],
        RATINGS_TAB: 1,
        ratingScales: [],
        canDownload: JSON.parse(localStorage.getItem('permissions') || '[]').includes('download_data'),
        columns: [
          { key: 'name', label: 'Name' },
          { key: 'duration_minutes', label: 'Duration' },
          { key: 'rating', label: 'Ratings' },
          { key: 'players', label: 'Players' },
        ],
        filterValues: {
          archived: false,
          name: '',
          rating: '',
          duration: { from: '', to: '' },
          public: '',
        },
        dialog: {
          show: false,
          title: '',
          message: '',
          variant: 'warning',
          showCancel: false,
          confirmText: 'OK',
          confirmVariant: 'primary',
          onConfirm: null,
          redirectHome: false,
        },
      }
    },
    computed: {
      filterSchema() {
        return [
          // Switch on lists archived events instead of active ones
          { key: 'archived', type: 'toggle', label: 'Archived', primary: true },
          { key: 'name', type: 'text', label: 'Name', placeholder: 'Any part of the name', operatorLabel: 'contains', primary: true },
          // One option per scale value: "Difficulty: 3", "Scare level: Intermediate". Value "<scale id>:<n>".
          { key: 'rating', type: 'select', label: 'Rating', primary: true, options: this.ratingFilterOptions },
          { key: 'duration', type: 'numberrange', label: 'Duration (minutes)', primary: true },
          {
            key: 'public', type: 'select', label: 'Public booking', primary: true,
            options: [
              { label: 'Public', value: 'yes' },
              { label: 'Not public', value: 'no' },
            ],
          },
        ]
      },
      ratingFilterOptions() {
        const options = []
        this.ratingScales.forEach(scale => {
          if (scale.display_type === 'labels') {
            scale.levels.forEach((level, i) => options.push({ label: scale.name + ': ' + level, value: scale.id + ':' + (i + 1) }))
          } else {
            for (let n = 0; n <= scale.max_value; n++) options.push({ label: scale.name + ': ' + n, value: scale.id + ':' + n })
          }
        })
        return options
      },
      hasActiveFilters() {
        const f = this.filterValues
        const d = f.duration || {}
        const has = v => v !== '' && v != null
        return !!f.name || has(f.rating) || has(d.from) || has(d.to) || !!f.public
      },
      // Archived switch: on lists archived events, off lists active ones.
      status() {
        return this.filterValues.archived ? 'archived' : 'active'
      },
      canReorder() {
        return this.status === 'active'
      },
      emptyState() {
        if (this.hasActiveFilters) {
          return { image: '/img/pandas/empty-events.png', title: 'No events match these filters', text: 'Try a different name, rating or duration.' }
        }
        if (this.status === 'archived') {
          return { image: '/img/pandas/empty-events.png', title: 'No archived events', text: 'Events you deactivate show up here.' }
        }
        return { image: '/img/pandas/empty-events.png', title: 'No events yet', text: 'Add your first event to start taking bookings.' }
      },
    },
    methods: {
      // Active rating scales, for the Rating filter options and the CSV columns.
      async loadRatingScales() {
        try {
          const config = await this.authConfig()
          const response = await axios.get(this.groupUrl() + '/rating-scales', config)
          this.ratingScales = (response.data.scales || []).filter(scale => !scale.archived)
        } catch (err) {
          console.log(err)
        }
      },
      // A tab is a different list, so its rows are cleared first and the table
      // shows skeleton rows until the new tab loads. Filter changes keep the
      // current rows and only dim them, since the list is the same.
      onTab(index) {
        if (index === this.activeTab) return
        this.activeTab = index
        this.$router.replace({ query: { ...this.$route.query, tab: index === this.RATINGS_TAB ? 'ratings' : undefined } }).catch(() => {})
      },
      addRatingScale() {
        this.$refs.ratingsPanel && this.$refs.ratingsPanel.openCreate()
      },
      // The filter bar emits one initial change after resolving saved views; that
      // is the first fetch. mounted() falls back if it never fires.
      onFiltersChanged(values) {
        this.filterValues = { ...this.filterValues, ...values, archived: values.archived === true }
        this.didInitialFetch = true
        this.fetchGames()
      },
      showDialog({ title, message, variant = 'warning', showCancel = false, confirmText = 'OK', confirmVariant = 'primary', onConfirm = null, redirectHome = false }) {
        this.dialog = { show: true, title, message, variant, showCancel, confirmText, confirmVariant, onConfirm, redirectHome }
      },
      onDialogConfirm() {
        const { onConfirm, redirectHome } = this.dialog
        this.dialog.show = false
        if (onConfirm) {
          onConfirm()
        } else if (redirectHome) {
          this.$router.push('/')
        }
      },
      // 401 and 500 are permission and plan gates on every games endpoint; both
      // send the user home after they acknowledge. Anything else stays on the page.
      handleRequestError(err, fallbackMessage) {
        const status = err && err.response ? err.response.status : 0
        if (status === 401) {
          this.showDialog({ title: 'No access', message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.', variant: 'danger', redirectHome: true })
          return true
        }
        if (status === 500) {
          this.showDialog({ title: 'Not included in your plan', message: 'The feature has not been enabled as part of your subscription plan.', variant: 'danger', redirectHome: true })
          return true
        }
        if (fallbackMessage) {
          this.showDialog({ title: 'Something went wrong', message: fallbackMessage, variant: 'danger' })
          return true
        }
        return false
      },
      async authConfig() {
        const session = await Auth.currentSession()
        return { headers: { Authorization: session.idToken.jwtToken } }
      },
      groupUrl() {
        const companyId = localStorage.getItem('companyId')
        const companyGroupId = JSON.parse(localStorage.getItem('group')).id
        return API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId
      },
      listQuery() {
        const f = this.filterValues
        const d = f.duration || {}
        const has = v => v !== '' && v != null
        const params = new URLSearchParams()
        params.set('status', this.status)
        if (f.name) params.set('name', String(f.name).trim())
        if (has(f.rating)) {
          const [scaleId, value] = String(f.rating).split(':')
          params.set('rating_scale_id', scaleId)
          params.set('rating', value)
        }
        if (has(d.from)) params.set('duration_from', String(d.from))
        if (has(d.to)) params.set('duration_to', String(d.to))
        if (f.public === 'yes') params.set('public', '1')
        if (f.public === 'no') params.set('public', '0')
        return this.groupUrl() + '/games?' + params.toString()
      },
      normalizeGame(g) {
        return { ...g, img: g.img === null ? PLACEHOLDER_IMG : g.img }
      },
      async fetchGames() {
        this.refreshing = true
        try {
          const config = await this.authConfig()
          const response = await axios.get(this.listQuery(), config)
          this.games = (Array.isArray(response.data) ? response.data : []).map(this.normalizeGame)
        } catch (err) {
          console.log(err)
          this.handleRequestError(err, 'The events could not be loaded. Please refresh and try again.')
        } finally {
          this.refreshing = false
        }
      },
      // Optimistic reorder: the new order shows the instant the row drops and
      // the save runs in the background with no loading state. With no filter
      // the list on screen is the full stored order and is saved as is. With a
      // filter, the full active list is fetched, the visible rows are refilled
      // into the stored positions they already occupy in their new order, and
      // that is saved. A short toast confirms the save; only a failed save
      // touches the table, by reloading the true order.
      async onReorder(rows) {
        const wasFullList = !this.hasActiveFilters
        this.games = rows
        try {
          const config = await this.authConfig()
          let active = rows
          if (!wasFullList) {
            const response = await axios.get(this.groupUrl() + '/games', config)
            active = (Array.isArray(response.data) ? response.data : []).filter(g => !Number(g.archived))
            const visible = new Set(rows.map(r => r.id))
            const slots = []
            active.forEach((g, i) => { if (visible.has(g.id)) slots.push(i) })
            if (slots.length !== rows.length) throw new Error('list changed')
            rows.forEach((row, k) => { active[slots[k]] = row })
          }
          await axios.put(this.groupUrl() + '/games', { games: active.map(g => ({ id: g.id })) }, config)
          this.$notify({ type: 'success', message: 'Event order saved', icon: 'tim-icons icon-check-2', timeout: 2000 })
        } catch (err) {
          console.log(err)
          if (!this.handleRequestError(err)) {
            this.$notify({ type: 'danger', message: 'Could not save the new event order', icon: 'tim-icons icon-alert-circle-exc' })
          }
          this.fetchGames()
        }
      },
      toggleActivationPrompt(item) {
        const archiving = !item.archived
        this.showDialog({
          title: 'Are you sure?',
          message: archiving
            ? 'This action will archive the event. It will no longer be available for bookings or waivers.'
            : 'This action will unarchive the event. It will become immediately available for bookings and waivers.',
          variant: 'warning',
          showCancel: true,
          confirmText: 'Confirm',
          confirmVariant: archiving ? 'danger' : 'primary',
          onConfirm: () => this.toggleArchive(item, archiving ? 1 : 0),
        })
      },
      async toggleArchive(item, archived) {
        this.refreshing = true
        try {
          const config = await this.authConfig()
          await axios.put(this.groupUrl() + '/games/' + item.id + '/archive', { archive: archived }, config)
          this.$notify({ type: 'success', message: archived ? 'Event deactivated' : 'Event activated', icon: 'tim-icons icon-check-2' })
          this.fetchGames()
        } catch (err) {
          this.refreshing = false
          console.log(err)
          const status = err && err.response ? err.response.status : 0
          if (status === 409) {
            this.showDialog({ title: 'Slot creation in progress', message: typeof err.response.data === 'string' ? err.response.data : 'Slot creation is still in progress for this event. Wait for it to finish before archiving.', variant: 'warning' })
          } else {
            this.handleRequestError(err, 'The event could not be updated. Please try again.')
          }
        }
      },
      addGame() {
        this.$router.push({ path: '/games/edit', query: { id: '' } })
      },
      editGame(game) {
        this.$router.push({ path: '/games/edit', query: { id: game.id } })
      },
      async duplicateGame(item) {
        this.refreshing = true
        try {
          const config = await this.authConfig()
          const response = await axios.put(this.groupUrl() + '/games/' + item.id, { duplicate: true }, config)
          this.refreshing = false
          this.$notify({ type: 'success', message: 'Event duplicated. You are now editing the copy.', icon: 'tim-icons icon-check-2' })
          this.$router.push({ path: '/games/edit', query: { id: response.data } })
        } catch (err) {
          this.refreshing = false
          console.log(err)
          this.handleRequestError(err, 'The event could not be duplicated. Please try again.')
        }
      },
      // Exports every row the current tab and filters show.
      downloadEvents() {
        try {
          const yesNo = v => (Number(v) ? 'Yes' : 'No')
          downloadCsv(this.status === 'archived' ? 'archived-events.csv' : 'events.csv', [
            { label: 'Name', value: g => g.name || '' },
            { label: 'Duration (minutes)', value: g => g.duration_minutes || '' },
            ...this.ratingScales.map(scale => ({
              label: scale.name,
              value: g => {
                const r = (g.ratings || []).find(x => x.rating_scale_id === scale.id)
                return r ? r.display + (r.show_to_customers ? '' : ' (hidden from customers)') : ''
              },
            })),
            { label: 'Min players', value: g => g.min_players_count == null ? '' : g.min_players_count },
            { label: 'Max players', value: g => g.max_players_count == null ? '' : g.max_players_count },
            { label: 'Public booking', value: g => yesNo(g.is_public_booking) },
            { label: 'Archived', value: g => yesNo(g.archived) },
          ], this.games)
        } catch (err) {
          console.log(err)
          this.$notify({ type: 'danger', message: 'Failed to export events', icon: 'tim-icons icon-alert-circle-exc' })
        }
      },
    },
    watch: {
      // The onboarding panel can point at the Ratings tab while this page is already open.
      '$route.query.tab'(tab) {
        this.activeTab = tab === 'ratings' ? this.RATINGS_TAB : 0
      },
    },
    mounted() {
      if (this.$route.query.tab === 'ratings') this.activeTab = this.RATINGS_TAB
      this.loadRatingScales()
      this.refreshing = true
      setTimeout(() => {
        if (!this.didInitialFetch) {
          this.didInitialFetch = true
          this.fetchGames()
        }
      }, 800)
    },
  }
</script>
